import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, Button, DialogActions, Divider, Chip } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Table from "../../../components/table/table";
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceOrdersRequest, getInvoicesRequest } from "../../../redux/orders/slice";
import CloseIcon from "@mui/icons-material/Close";
import OrderTableStatusCell from "../../../components/order-table/status-cell";
import dayjs from "dayjs";
import "./invoices.css";
import { getProcessesProcedureSuccess, getProcessesProcedureRequest, getCurrentProcessesProcedureRequest, getCurrentProcessesProcedureSuccess } from "../../../redux/clients/slice";
import DispatchDialog from "./dispatch-dialog";
import { dispatchInvoiceRequest, partialDispatchInvoiceRequest } from "../../../utils/APICalls";
import Loading from "../../../components/loading/loading";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Notification from "../../../components/notification/notification";
import { userType } from "../../../utils/Constant";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import { ClearAllOutlined } from "@mui/icons-material";

interface InvoiceLineItemsDialogProps {
  showDialog: boolean;
  closeDialog: () => void;
  invoiceDetails: any;
  isClosed: boolean;
  invoiceId: string;
  updateInvoices?: any;
}

export default function InvoiceLineItemsDialog({
  showDialog,
  closeDialog,
  invoiceDetails,
  isClosed,
  invoiceId,
  updateInvoices,
}: InvoiceLineItemsDialogProps) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [selectedOrderAmount, setSelectedOrderAmount] = useState(0);
  const [billingContact, setBillingContact] = useState<any[]>([]);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { loading, invoiceOrders } = useSelector((state: any) => state.orders);
  const { processesProcedure: proceduresResponse, currentProcessesProcedure } = useSelector(
    (state: any) => state?.clients
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(invoiceOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (invoiceOrders?.totalCount !== undefined) {
      rowCountRef.current = invoiceOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [invoiceOrders?.totalCount]);

  const column: GridColDef[] = isClosed ? [
    {
      field: "invoiceLineItemNumber",
      headerName: "Invoice Line Item #",
      width: 210,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.invoiceLineItemNumber || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="invoiceLineItemNumber"
          sortField="invoiceLineItemNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order #",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.orderNumber || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "formName",
      headerName: "Form Name",
      width: 160,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="formName"
          sortField="formname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Client Fee Amount",
      width: 210,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", pt: 2 }}>
          ${parseFloat(params.row.totalAmount).toFixed(2) || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="totalAmount"
          sortField="totalAmount"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "loanNumber",
      headerName: "Loan #",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.loanNumber || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="loanNumber"
          sortField="loannumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) => row?.propertyRecord?.address2 ?
        `${row?.propertyRecord?.street}, ${row?.propertyRecord?.address2}` : row?.propertyRecord?.street,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="address"
          sortField="address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.city || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.state || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.zip || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "requester",
      headerName: "Requestor",
      width: 150,
      sortable: false,
      valueFormatter: (value?: any) =>
        value?.fullName || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="requester"
          sortField="requester"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "option1",
      headerName: "Option 1",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option1"
          sortField="option1"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "option2",
      headerName: "Option 2",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option2"
          sortField="option2"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "option3",
      headerName: "Option 3",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option3"
          sortField="option3"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
  ] : [
    {
      field: "option1",
      headerName: "Option 1",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option1"
          sortField="option1"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "option2",
      headerName: "Option 2",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option2"
          sortField="option2"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "option3",
      headerName: "Option 3",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string) =>
        value || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="option3"
          sortField="option3"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Client Fee Amount",
      width: 210,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", pt: 2 }}>
          ${parseFloat(params.row.totalAmount).toFixed(2) || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="totalAmount"
          sortField="totalAmount"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order #",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.orderNumber || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "formName",
      headerName: "Form Name",
      width: 160,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="formName"
          sortField="formname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "loanNumber",
      headerName: "Loan #",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.loanNumber || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="loanNumber"
          sortField="loannumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) => row?.propertyRecord?.address2 ?
        `${row?.propertyRecord?.street}, ${row?.propertyRecord?.address2}` : row?.propertyRecord?.street,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="address"
          sortField="address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.city || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.state || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 150,
      sortable: false,
      valueFormatter: (value?: string, row?: any) =>
        row?.propertyRecord?.zip || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "requester",
      headerName: "Requestor",
      width: 150,
      sortable: false,
      valueFormatter: (value?: any) =>
        value?.fullName || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="requester"
          sortField="requester"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    ...(invoiceDetails?.projectName
      ? [
        {
          field: "orderStatus",
          headerName: "Order Status",
          width: 180,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          renderCell: (params: GridRenderCellParams<any>) => (
            <OrderTableStatusCell rowData={params.row} />
          ),
          renderHeader: (params: any) => (
            <TableHeaderSelectField
              field="orderStatus"
              sortField="orderStatus"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterItems}
              values={[
                "OrderAdded",
                "OrderReview",
                "OrderPending",
                "AutoValidation",
                "ManualValidation",
                "AutoAssign",
                "ManualAssign",
                "Recruiting",
                "InspectorAssigned",
                "NoAppointment",
                "FutureAppointment",
                "PastAppointment",
                "InspectorReturn",
                "UnassignedQC",
                "AssignedQC",
                "FinalQC",
                "ResultsDelivered",
                "DeliveryQC",
                "ToBeInvoiced",
                "Invoiced",
                "ClientReturns",
                "OnHold",
                "Closed",
              ]}
            />
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    if (showDialog && invoiceId) {
      setPaginationModel({
        page: 0,
        pageSize: 10,
      });
      setRowData([]);
    } else if (!showDialog) {
      setRowData([]);
    }
  }, [showDialog, invoiceId]); // eslint-disable-line

  useEffect(() => {
    setRowData(invoiceOrders?.items || []);
  }, [invoiceOrders]);

  useEffect(() => {
    if (paginationModel) {
      setFilterParams((prev) => ({
        ...prev,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }));
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    if (!showDialog || !invoiceId) {
      return;
    }

    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });

    setRowData([]);
    rowCountRef.current = 0;

    if ("page" in filters) {
      dispatch(
        getInvoiceOrdersRequest({
          invoiceId: invoiceId,
          params: filters
        })
      );
    }
    /* eslint-disable */
  }, [filterParams]); /* eslint-enable */

  useEffect(() => {
    let totalAmount = 0;
    selectedRowData.forEach(data => {
      totalAmount += data?.totalAmount;
    });
    setSelectedOrderAmount(totalAmount);
  }, [selectedRowData]);

  useEffect(() => {
    if (currentUser?.userType === userType.tenant) {
      dispatch(getProcessesProcedureSuccess({ response: null }));
      if (invoiceDetails) {
        dispatch(getProcessesProcedureRequest({ id: invoiceDetails?.clientId }));
      }
    } else if (currentUser?.userType === userType.client) {
      dispatch(getCurrentProcessesProcedureSuccess({ response: null }))
      dispatch(getCurrentProcessesProcedureRequest())
    }
  }, [invoiceDetails]) // eslint-disable-line

  useEffect(() => {
    if (currentUser?.userType === userType.tenant) {
      if (proceduresResponse) {
        if (proceduresResponse?.notificationEvents?.submitInvoice?.client) {
          setBillingContact(proceduresResponse?.notificationEvents?.submitInvoice?.client?.recipients);
        }
        if (proceduresResponse?.notificationEvents?.submitInvoice?.subDivisions) {
          const filterSubDivision = proceduresResponse?.notificationEvents?.submitInvoice?.subDivisions?.find((division: any) => division?.organizationId === invoiceDetails?.subDivisionId);
          if (filterSubDivision) {
            setBillingContact(filterSubDivision?.recipients);
          } else {
            setBillingContact([]);
          }
        }
      }
    } else if (currentUser?.userType === userType.client) {
      if (currentProcessesProcedure) {
        if (currentProcessesProcedure?.notificationEvents?.submitInvoice?.client) {
          setBillingContact(currentProcessesProcedure?.notificationEvents?.submitInvoice?.client?.recipients);
        }
        if (currentProcessesProcedure?.notificationEvents?.submitInvoice?.subDivisions) {
          const filterSubDivision = currentProcessesProcedure?.notificationEvents?.submitInvoice?.subDivisions?.find((division: any) => division?.organizationId === invoiceDetails?.subDivisionId);
          if (filterSubDivision) {
            setBillingContact(filterSubDivision?.recipients);
          } else {
            setBillingContact([]);
          }
        }
      }
    }
  }, [proceduresResponse, currentProcessesProcedure]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterItems = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const confirmAllDispatch = async () => {
    setShowLoading(true);
    try {
      const payload = {
        invoiceIds: [invoiceId]
      }
      const response = await dispatchInvoiceRequest(payload);

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        setShowDispatchDialog(false);
        setRowSelectionModel([]);
        dispatch(getInvoicesRequest({
          page: 1,
          pageSize: 10,
        }));
        closeDialog();
        setSuccessMessage("Invoice dispatched successfully!");
        setShowSuccessNotification(true);
      } else {
        const errMsg =
          (response && (response as any).data?.detail) ||
          (response && (response as any).data?.message) ||
          "Failed to dispatch invoice. Please try again.";
        setErrorMessage(errMsg);
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setShowLoading(false);
    }
  };

  const confirmPartialDispatch = async () => {
    setShowLoading(true);
    try {
      const payload = {
        invoiceOrderIds: rowSelectionModel
      }
      const response = await partialDispatchInvoiceRequest(invoiceId, payload);

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        setShowDispatchDialog(false);
        setRowSelectionModel([]);
        setSelectedOrderAmount(0);
        dispatch(getInvoiceOrdersRequest({
          invoiceId: invoiceId,
          params: filterParams
        }));
        updateInvoices();
        setSuccessMessage("Invoice dispatched successfully!");
        setShowSuccessNotification(true);
        if (rowSelectionModel.length === invoiceOrders?.totalCount) {
          // if all orders are dispatch then refresh the master invoice table
          dispatch(getInvoicesRequest({
            page: 1,
            pageSize: 10,
          }));
        }
      } else {
        const errMsg =
          (response && (response as any).data?.detail) ||
          (response && (response as any).data?.message) ||
          "Failed to dispatch invoice. Please try again.";
        setErrorMessage(errMsg);
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setShowLoading(false);
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    const baseFilters: any = {
      page: 1,
      pageSize: 10,
    };
    setFilterParams(baseFilters);
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={() => closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={"lg"}
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
          <Box>
            <Typography variant="h6">{invoiceDetails?.clientName} - {invoiceDetails?.invoiceType} {invoiceDetails?.projectName && `- ${invoiceDetails?.projectName}`}</Typography>
            <Box display={"flex"} mt={0.25}>
              <Typography><b>Billing Contact:</b></Typography>
              {billingContact.length === 0 && (
                <Typography ml={1}>-</Typography>
              )}
              {billingContact.map((contact) => (
                <Chip
                  sx={{ ml: 1 }}
                  key={contact.id}
                  label={contact?.fullName}
                  variant="outlined"
                  color="primary"
                  size="small"
                ></Chip>
              ))}
            </Box>
            {isClosed && (
              <Box display={"flex"} alignItems="end" mt={0.25}>
                <Typography><b>Invoice #:</b></Typography>
                <Typography ml={1}>{invoiceDetails?.invoiceNumber}</Typography>
              </Box>
            )}
            <Box display={"flex"} alignItems="end">
              <Typography mt={0.25}><b>{isClosed ? "Total Invoice Amount:" : "All Orders Total:"}</b></Typography>
              <Typography ml={1}>${parseFloat(invoiceDetails?.totalAmount as any)?.toFixed(2) || "0.00"}</Typography>
            </Box>
            {isClosed ? (
              <Box display={"flex"} alignItems="end">
                <Typography mt={0.25}><b>Invoiced Date:</b></Typography>
                <Typography ml={1}>{dayjs(invoiceDetails?.closedAt).format("MMM DD, YYYY")}</Typography>
              </Box>
            ) : (
              <Box display={"flex"} alignItems="end">
                <Typography mt={0.25}><b>Selected Orders Total:</b></Typography>
                <Typography ml={1}>${parseFloat(selectedOrderAmount as any).toFixed(2) || "0.00"}</Typography>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 12,
            top: 12,
            color: "rgba(0, 0, 0, 0.54)",
          })}
        >
          <CloseIcon />
        </IconButton>
        <Divider />
        <DialogContent sx={{ py: 1 }}>
          <Box sx={{ p: 0, overflow: "hidden" }}>
            <Box
              className="clients-header"
              sx={{
                mb: 0.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
              }}
            >
              <Box display={"flex"} alignItems={"center"}>
                {/* Empty space for left side */}
              </Box>
              <Box>
                <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
                  <ClearAllOutlined color="primary" />
                </IconButton>
                <IconButton
                  aria-label="filter"
                  onClick={() => onFilterClick(!showTableFilter)}
                >
                  {showTableFilter ? (
                    <FilterAltOutlinedIcon color="primary" />
                  ) : (
                    <FilterAltOutlinedIcon color="primary" />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ height: "calc(100% - 120px)" }}>
              <Table
                key={"invoice-orders-table-" + filterResetKey}
                rowData={rowData}
                rowCount={rowCount}
                loading={loading}
                columnDef={column}
                headerHeight={headerHeight}
                disableColumnMenu={true}
                disableVirtualization={true}
                paginationModel={paginationModel}
                onPaginationModelChange={(newPaginationModel: any) => {
                  setPaginationModel(newPaginationModel);
                }}
                checkboxSelection={isClosed ? false : true}
                rowHeight={50}
                onRowSelectionModelChange={(newRowSelectionModel: any) => {
                  setRowSelectionModel(newRowSelectionModel);
                  const selected = newRowSelectionModel.map((id: string) => {
                    const findRow = rowData.find((r: any) => r.id === id)
                    if (findRow) return findRow
                    else {
                      const findRow = selectedRowData.find((r: any) => r.id === id)
                      return findRow;
                    }
                  });
                  setSelectedRowData(selected);
                }}
                rowSelectionModel={rowSelectionModel}
                isRowSelectable={(params: any) => invoiceDetails?.projectName ? params.row.orderStatus === 'ToBeInvoiced' ? true : false : true}
                getRowClassName={(params: any) =>
                  !isClosed && (invoiceDetails?.projectName ? params.row.orderStatus === 'ToBeInvoiced' ? '' : 'disabled-row' : '')
                }
                keepNonExistentRowsSelected
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ mr: 1 }}>
          {isClosed ? (
            <>
              <Button variant="contained" onClick={closeDialog}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={closeDialog}>
                Cancel
              </Button>
              {rowSelectionModel.length > 0 ? (
                <Button variant="contained" onClick={() => setShowDispatchDialog(true)}>
                  Dispatch with Selected Orders
                </Button>
              ) : (
                <Button variant="contained" onClick={() => setShowDispatchDialog(true)}
                  disabled={invoiceDetails?.hasOpenOrders}>
                  Dispatch with All orders
                </Button>
              )}
            </>
          )}

        </DialogActions>
      </Dialog>

      <DispatchDialog
        show={showDispatchDialog}
        close={() => setShowDispatchDialog(false)}
        title={`Are you sure you want to dispatch this invoice with the ${rowSelectionModel.length > 0 ? "selected" : "all"} orders?`}
        onConfirmDispatch={rowSelectionModel.length > 0 ? confirmPartialDispatch : confirmAllDispatch}
        isFromInvoiceTable={false}
      />
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </>
  );
}
